import type { Rule } from "../../../hooks/react-query/backOffice/pricingAndRating/useRules";

// Mock data for rule types
export const mockRuleTypes = [
  {
    id: 1,
    code: "ITEM_1375",
    displayName: "ITEM_1375 - Linear Foot Rule",
    description: "Linear Foot Rule",
  },
  {
    id: 2,
    code: "ITEM_1360",
    displayName: "ITEM_1360 - Minimum Charge Cubic Capacity and Density",
    description: "Minimum Charge Cubic Capacity and Density",
  },
  {
    id: 3,
    code: "ITEM_1400",
    displayName:
      "ITEM_1400 - Residential Delivery - Auto Application of Liftgate Rule",
    description: "Residential Delivery - Auto Application of Liftgate Rule",
  },
];

export const mockRateClasses = [
  {
    id: 77,
    code: "50",
    displayName: "Class 50",
    description: "Tariff Class 50",
  },
  {
    id: 76,
    code: "55",
    displayName: "Class 55",
    description: "Tariff Class 55",
  },
  {
    id: 75,
    code: "60",
    displayName: "Class 60",
    description: "Tariff Class 60",
  },
  {
    id: 74,
    code: "65",
    displayName: "Class 65",
    description: "Tariff Class 65",
  },
  {
    id: 73,
    code: "70",
    displayName: "Class 70",
    description: "Tariff Class 70",
  },
  {
    id: 72,
    code: "77.5",
    displayName: "Class 77.5",
    description: "Tariff Class 77.5",
  },
  {
    id: 71,
    code: "85",
    displayName: "Class 85",
    description: "Tariff Class 85",
  },
  {
    id: 70,
    code: "92.5",
    displayName: "Class 92.5",
    description: "Tariff Class 92.5",
  },
  {
    id: 69,
    code: "100",
    displayName: "Class 100",
    description: "Tariff Class 100",
  },
  {
    id: 68,
    code: "110",
    displayName: "Class 110",
    description: "Tariff Class 110",
  },
  {
    id: 67,
    code: "125",
    displayName: "Class 125",
    description: "Tariff Class 125",
  },
];

// Mock data for applied accessorial dropdown options
export const appliedAccessorialOptions = [
  { value: "LIFTGATE_DELIVERY", label: "Liftgate Delivery" },
  { value: "INSIDE_DELIVERY", label: "Inside Delivery" },
  { value: "RESIDENTIAL_DELIVERY", label: "Residential Delivery" },
];

// Shared mock details for all rule types
const mockRuleDetailsBase = {
  name: "Standard Minimum",
  description: "Standard default rate for all shipments",
  effectiveDate: "11/05/2025",
  expirationDate: "11/05/2026",
  version: "Version 5",
};

// Mock data for rule detail (for edit functionality)
export const mockRuleDetails = {
  item1360: {
    ruleType: "ITEM_1360",
    details: mockRuleDetailsBase,
    item1360Config: {
      cubicFeetThreshold: "500",
      densityThreshold: "12",
      calculatedDensity: "10",
      fallbackDiscount: "5",
      ratedClass: "60",
    },
  },
  item1375: {
    ruleType: "ITEM_1375",
    details: mockRuleDetailsBase,
    item1375Config: {
      linearFeetThreshold: "40",
      ratedClass: "65",
      ratedWeight: "200",
    },
  },
  item1400: {
    ruleType: "ITEM_1400",
    details: mockRuleDetailsBase,
    item1400Config: {
      weightThreshold: "100",
      appliedAccessorial: "LIFTGATE_DELIVERY",
    },
  },
};

// Generate comprehensive mock rules based on API contract
export const mockRules: Rule[] = [
  {
    id: 123,
    name: "Standard Minimum",
    ruleType: "ITEM_1360",
    ruleTypeDisplay: "ITEM_1360 - Minimum Charge Cubic Capacity and Density",
    description: "Standard default rate for all shipments",
    effectiveDate: "11/05/2025",
    expirationDate: "11/05/2026",
    version: 5,
    status: "Active",
    createdDate: "2025-10-15T10:30:00Z",
    createdBy: "John Doe",
    modifiedDate: "2025-11-01T14:45:00Z",
    modifiedBy: "Jane Smith",
  },
  {
    id: 124,
    name: "Standard Linear",
    ruleType: "ITEM_1375",
    ruleTypeDisplay: "ITEM_1375 - Linear Foot Rule",
    description: "Standard linear rate for freight",
    effectiveDate: "11/05/2025",
    expirationDate: "11/05/2026",
    version: 5,
    status: "Active",
    createdDate: "2025-10-15T10:30:00Z",
    createdBy: "John Doe",
    modifiedDate: null,
    modifiedBy: null,
  },
  {
    id: 125,
    name: "Express Delivery Rule",
    ruleType: "ITEM_1375",
    ruleTypeDisplay: "ITEM_1375 - Linear Foot Rule",
    description: "Premium express delivery charges",
    effectiveDate: "10/01/2025",
    expirationDate: "10/01/2026",
    version: 3,
    status: "Active",
    createdDate: "2025-09-15T09:20:00Z",
    createdBy: "Alice Johnson",
    modifiedDate: "2025-10-05T11:30:00Z",
    modifiedBy: "Bob Wilson",
  },
  {
    id: 126,
    name: "Holiday Surcharge",
    ruleType: "ITEM_1360",
    ruleTypeDisplay: "ITEM_1360 - Minimum Charge Cubic Capacity and Density",
    description: "Additional charges during holiday season",
    effectiveDate: "12/01/2025",
    expirationDate: "01/15/2026",
    version: 1,
    status: "Draft",
    createdDate: "2025-11-20T14:00:00Z",
    createdBy: "Carol Martinez",
    modifiedDate: null,
    modifiedBy: null,
  },
  {
    id: 127,
    name: "Hazmat Handling",
    ruleType: "ITEM_1400",
    ruleTypeDisplay:
      "ITEM_1400 - Residential Delivery - Auto Application of Liftgate Rule",
    description: "Special handling fee for hazardous materials",
    effectiveDate: "01/01/2025",
    expirationDate: "12/31/2026",
    version: 8,
    status: "Active",
    createdDate: "2024-12-01T08:00:00Z",
    createdBy: "David Lee",
    modifiedDate: "2025-06-15T16:20:00Z",
    modifiedBy: "Eve Chen",
  },
  {
    id: 128,
    name: "Weekend Pickup",
    ruleType: "ITEM_1400",
    ruleTypeDisplay:
      "ITEM_1400 - Residential Delivery - Auto Application of Liftgate Rule",
    description: "Weekend and after-hours pickup charges",
    effectiveDate: "09/01/2025",
    expirationDate: "09/01/2026",
    version: 2,
    status: "Inactive",
    createdDate: "2025-08-10T10:15:00Z",
    createdBy: "Frank Brown",
    modifiedDate: "2025-08-25T13:45:00Z",
    modifiedBy: "Grace Taylor",
  },
  {
    id: 129,
    name: "Residential Delivery",
    ruleType: "ITEM_1360",
    ruleTypeDisplay: "ITEM_1360 - Minimum Charge Cubic Capacity and Density",
    description: "Additional fee for residential address deliveries",
    effectiveDate: "07/01/2025",
    expirationDate: "07/01/2026",
    version: 4,
    status: "Active",
    createdDate: "2025-06-01T12:00:00Z",
    createdBy: "Henry White",
    modifiedDate: "2025-06-20T15:10:00Z",
    modifiedBy: "Iris Green",
  },
  {
    id: 130,
    name: "Liftgate Service",
    ruleType: "ITEM_1400",
    ruleTypeDisplay:
      "ITEM_1400 - Residential Delivery - Auto Application of Liftgate Rule",
    description: "Charge for liftgate equipment use",
    effectiveDate: "08/01/2025",
    expirationDate: "08/01/2026",
    version: 6,
    status: "Active",
    createdDate: "2025-07-10T09:30:00Z",
    createdBy: "Jack Miller",
    modifiedDate: "2025-07-25T14:00:00Z",
    modifiedBy: "Karen Davis",
  },
  {
    id: 131,
    name: "Inside Delivery",
    ruleType: "ITEM_1400",
    ruleTypeDisplay:
      "ITEM_1400 - Residential Delivery - Auto Application of Liftgate Rule",
    description: "Charge for delivery inside premises",
    effectiveDate: "06/01/2025",
    expirationDate: "06/01/2026",
    version: 3,
    status: "Draft",
    createdDate: "2025-05-15T11:20:00Z",
    createdBy: "Laura Anderson",
    modifiedDate: "2025-05-28T10:00:00Z",
    modifiedBy: "Mike Thomas",
  },
  {
    id: 132,
    name: "Fuel Adjustment",
    ruleType: "ITEM_1360",
    ruleTypeDisplay: "ITEM_1360 - Minimum Charge Cubic Capacity and Density",
    description: "Weekly fuel price adjustment calculation",
    effectiveDate: "01/01/2025",
    expirationDate: null,
    version: 12,
    status: "Active",
    createdDate: "2024-12-15T07:45:00Z",
    createdBy: "Nancy Jackson",
    modifiedDate: "2025-10-01T09:15:00Z",
    modifiedBy: "Oscar Martin",
  },
  {
    id: 133,
    name: "Cold Chain Storage",
    ruleType: "ITEM_1360",
    ruleTypeDisplay: "ITEM_1360 - Minimum Charge Cubic Capacity and Density",
    description: "Temperature-controlled storage and transport",
    effectiveDate: "11/01/2025",
    expirationDate: "11/01/2026",
    version: 2,
    status: "Active",
    createdDate: "2025-10-01T13:30:00Z",
    createdBy: "Patricia Garcia",
    modifiedDate: null,
    modifiedBy: null,
  },
  {
    id: 134,
    name: "White Glove Service",
    ruleType: "ITEM_1360",
    ruleTypeDisplay: "ITEM_1360 - Minimum Charge Cubic Capacity and Density",
    description: "Premium white glove delivery service",
    effectiveDate: "12/01/2025",
    expirationDate: "12/01/2026",
    version: 1,
    status: "Draft",
    createdDate: "2025-11-15T16:00:00Z",
    createdBy: "Quinn Rodriguez",
    modifiedDate: null,
    modifiedBy: null,
  },
  {
    id: 135,
    name: "Oversize Freight",
    ruleType: "ITEM_1360",
    ruleTypeDisplay: "ITEM_1360 - Minimum Charge Cubic Capacity and Density",
    description: "Special handling for oversized freight items",
    effectiveDate: "05/01/2025",
    expirationDate: "05/01/2026",
    version: 7,
    status: "Inactive",
    createdDate: "2025-04-01T10:00:00Z",
    createdBy: "Rachel Wilson",
    modifiedDate: "2025-04-20T14:30:00Z",
    modifiedBy: "Sam Moore",
  },
  {
    id: 136,
    name: "Limited Access",
    ruleType: "ITEM_1360",
    ruleTypeDisplay: "ITEM_1360 - Minimum Charge Cubic Capacity and Density",
    description: "Additional fee for limited access locations",
    effectiveDate: "09/15/2025",
    expirationDate: "09/15/2026",
    version: 4,
    status: "Active",
    createdDate: "2025-08-20T11:45:00Z",
    createdBy: "Tom Taylor",
    modifiedDate: "2025-09-01T13:20:00Z",
    modifiedBy: "Uma Anderson",
  },
  {
    id: 137,
    name: "Assembly Required",
    ruleType: "ITEM_1360",
    ruleTypeDisplay: "ITEM_1360 - Minimum Charge Cubic Capacity and Density",
    description: "Charge for on-site assembly services",
    effectiveDate: "10/01/2025",
    expirationDate: "10/01/2026",
    version: 3,
    status: "Active",
    createdDate: "2025-09-10T09:00:00Z",
    createdBy: "Victor Harris",
    modifiedDate: "2025-09-25T15:45:00Z",
    modifiedBy: "Wendy Clark",
  },
  {
    id: 138,
    name: "Documentation Fee",
    ruleType: "ITEM_1360",
    ruleTypeDisplay: "ITEM_1360 - Minimum Charge Cubic Capacity and Density",
    description: "Custom documentation and paperwork handling",
    effectiveDate: "11/10/2025",
    expirationDate: "11/10/2026",
    version: 2,
    status: "Draft",
    createdDate: "2025-10-25T14:15:00Z",
    createdBy: "Xavier Lewis",
    modifiedDate: null,
    modifiedBy: null,
  },
  {
    id: 139,
    name: "Redelivery Charge",
    ruleType: "ITEM_1400",
    ruleTypeDisplay:
      "ITEM_1400 - Residential Delivery - Auto Application of Liftgate Rule",
    description: "Charge for failed delivery reattempts",
    effectiveDate: "08/15/2025",
    expirationDate: "08/15/2026",
    version: 5,
    status: "Active",
    createdDate: "2025-07-20T10:30:00Z",
    createdBy: "Yolanda Walker",
    modifiedDate: "2025-08-01T12:00:00Z",
    modifiedBy: "Zach Hall",
  },
  {
    id: 140,
    name: "Appointment Delivery",
    ruleType: "ITEM_1400",
    ruleTypeDisplay:
      "ITEM_1400 - Residential Delivery - Auto Application of Liftgate Rule",
    description: "Fee for scheduled appointment deliveries",
    effectiveDate: "07/15/2025",
    expirationDate: "07/15/2026",
    version: 6,
    status: "Active",
    createdDate: "2025-06-25T08:45:00Z",
    createdBy: "Amy Allen",
    modifiedDate: "2025-07-05T11:15:00Z",
    modifiedBy: "Ben Young",
  },
  {
    id: 141,
    name: "Notification Service",
    ruleType: "ITEM_1400",
    ruleTypeDisplay:
      "ITEM_1400 - Residential Delivery - Auto Application of Liftgate Rule",
    description: "Advanced delivery notification service",
    effectiveDate: "09/20/2025",
    expirationDate: "09/20/2026",
    version: 1,
    status: "Inactive",
    createdDate: "2025-09-01T13:00:00Z",
    createdBy: "Cathy King",
    modifiedDate: "2025-09-10T16:30:00Z",
    modifiedBy: "Dan Wright",
  },
  {
    id: 142,
    name: "Storage in Transit",
    ruleType: "ITEM_1360",
    ruleTypeDisplay: "ITEM_1360 - Minimum Charge Cubic Capacity and Density",
    description: "Storage in transit charges per day",
    effectiveDate: "06/15/2025",
    expirationDate: "06/15/2026",
    version: 9,
    status: "Active",
    createdDate: "2025-05-20T09:15:00Z",
    createdBy: "Elena Scott",
    modifiedDate: "2025-06-01T14:20:00Z",
    modifiedBy: "Fred Adams",
  },
  {
    id: 143,
    name: "Protection Plan",
    ruleType: "ITEM_1360",
    ruleTypeDisplay: "ITEM_1360 - Minimum Charge Cubic Capacity and Density",
    description: "Extended cargo protection plan",
    effectiveDate: "10/20/2025",
    expirationDate: "10/20/2026",
    version: 3,
    status: "Draft",
    createdDate: "2025-10-05T11:30:00Z",
    createdBy: "Gina Baker",
    modifiedDate: null,
    modifiedBy: null,
  },
  {
    id: 144,
    name: "Cross-Dock Service",
    ruleType: "ITEM_1360",
    ruleTypeDisplay: "ITEM_1360 - Minimum Charge Cubic Capacity and Density",
    description: "Cross-dock facility handling charges",
    effectiveDate: "08/20/2025",
    expirationDate: "08/20/2026",
    version: 4,
    status: "Active",
    createdDate: "2025-07-25T10:45:00Z",
    createdBy: "Hugo Nelson",
    modifiedDate: "2025-08-10T13:15:00Z",
    modifiedBy: "Iris Carter",
  },
  {
    id: 145,
    name: "Sort and Segregate",
    ruleType: "ITEM_1360",
    ruleTypeDisplay: "ITEM_1360 - Minimum Charge Cubic Capacity and Density",
    description: "Sorting and segregation service charges",
    effectiveDate: "11/25/2025",
    expirationDate: "11/25/2026",
    version: 2,
    status: "Active",
    createdDate: "2025-11-10T15:00:00Z",
    createdBy: "Jake Mitchell",
    modifiedDate: null,
    modifiedBy: null,
  },
  {
    id: 146,
    name: "Palletizing Service",
    ruleType: "ITEM_1400",
    ruleTypeDisplay:
      "ITEM_1400 - Residential Delivery - Auto Application of Liftgate Rule",
    description: "Palletizing and wrapping service",
    effectiveDate: "07/20/2025",
    expirationDate: "07/20/2026",
    version: 5,
    status: "Inactive",
    createdDate: "2025-06-30T09:30:00Z",
    createdBy: "Kelly Perez",
    modifiedDate: "2025-07-10T14:45:00Z",
    modifiedBy: "Leo Roberts",
  },
  {
    id: 147,
    name: "Marking and Tagging",
    ruleType: "ITEM_1400",
    ruleTypeDisplay:
      "ITEM_1400 - Residential Delivery - Auto Application of Liftgate Rule",
    description: "Custom marking and tagging services",
    effectiveDate: "12/05/2025",
    expirationDate: "12/05/2026",
    version: 1,
    status: "Draft",
    createdDate: "2025-11-20T12:00:00Z",
    createdBy: "Maria Turner",
    modifiedDate: null,
    modifiedBy: null,
  },
  {
    id: 148,
    name: "Reconsignment Fee",
    ruleType: "ITEM_1400",
    ruleTypeDisplay:
      "ITEM_1400 - Residential Delivery - Auto Application of Liftgate Rule",
    description: "Fee for address changes after shipment",
    effectiveDate: "09/25/2025",
    expirationDate: "09/25/2026",
    version: 7,
    status: "Active",
    createdDate: "2025-09-05T10:20:00Z",
    createdBy: "Nathan Phillips",
    modifiedDate: "2025-09-15T13:40:00Z",
    modifiedBy: "Olivia Campbell",
  },
];
